/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { FeedStoreService } from '../../../../services/feed-store.service';
import { LikeService } from '../../../../services/like.service';

export interface LikeListDialogData {
  likeable: { id: number; type: string; likes: { count: number } };
  // The signed-in user's id, to mark "You" in the list.
  currentUserId?: number;
}

// Opens the like list: a centred dialog, or a sheet docked to the bottom on phones.
export function openLikeList(dialogService: DialogService, likeable: any, user?: any): void {
  const phone = window.matchMedia('(max-width: 425px)').matches;
  dialogService.open(CommunityLikeListDialogComponent, {
    data: { likeable, currentUserId: user?.id },
    ...(phone ? {
      position: { bottom: '0' }, width: '100vw', minWidth: '100vw', maxWidth: '100vw', panelClass: 'like-list-sheet',
    } : {}),
  });
}

// Lists the users who liked a community item. Replaces the AngularJS like-list overlay,
// community-user-list-item and view-more.
@Component({
  selector: 'ut-community-like-list-dialog',
  templateUrl: './community-like-list-dialog.component.html',
  styleUrls: ['./community-like-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityLikeListDialogComponent implements OnInit {
  users: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LikeListDialogData,
    private dialogRef: MatDialogRef<CommunityLikeListDialogComponent>,
    private feedStore: FeedStoreService,
    private likeService: LikeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.users = this.feedStore.create((params) => this.likeService.likedUsers(this.data.likeable, params));
    this.track(this.users.load());
  }

  get title(): string {
    const { count } = this.data.likeable.likes;
    return `${count} ${count === 1 ? 'Like' : 'Likes'}`;
  }

  loadMore(): void {
    this.track(this.users.loadMore());
  }

  close(): void {
    this.dialogRef.close();
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): void {
    this.cdr.markForCheck();
    promise.then(() => this.cdr.markForCheck());
  }
}
